/**
 * Composable para verificar sincronización entre datos locales (IndexedDB) y servidor
 */
import { ref } from 'vue'
import { useJsonDiff } from './useJsonDiff.js'
import api from '../services/api.js'
import { KEYS } from '../services/indexedDBService.js'

export function useSyncChecker() {
  const { compareArrays, compareObjects } = useJsonDiff()
  
  const isSyncing = ref(false)
  const syncResults = ref([])
  const hasConflicts = ref(false)

  /**
   * Mapeo de claves de IndexedDB a nombres de archivo en el servidor
   */
  const KEY_TO_FILENAME = {
    [KEYS.HISTORIAS]: 'historias.json',
    [KEYS.METODOS]: 'metodos.json',
    [KEYS.ESCENARIOS]: 'escenarios.json',
    [KEYS.REFERENCIAS]: 'referencias.json',
    [KEYS.VINCULOS]: 'vinculos.json',
    [KEYS.BOT_VINCULOS]: 'bot_vinculos.json',
    [KEYS.CLASE_VINCULOS]: 'clase_vinculos.json',
    [KEYS.OBJETOS]: 'objetos.json',
    [KEYS.RASGOS_PERSONALIDAD]: 'rasgos_personalidad.json',
    [KEYS.RASGOS_FISICOS]: 'rasgos_fisicos.json',
    [KEYS.RASGOS_CLASE]: 'rasgos_clase.json',
    [KEYS.RASGOS_ECONOMICOS]: 'rasgos_economicos.json',
    [KEYS.CLASE_RASGOS_ECONOMICOS]: 'clase_rasgos_economicos.json',
    [KEYS.AFINIDADES]: 'afinidades.json',
    [KEYS.OCUPACIONES]: 'ocupaciones.json',
    [KEYS.CLASE_OCUPACIONES]: 'clase_ocupaciones.json',
    [KEYS.APELLIDOS]: 'apellidos.json',
    [KEYS.EPILOGOS]: 'epilogos.json',
    [KEYS.PERSONAJES]: 'personajes.json',
    [KEYS.COLECCIONES]: 'colecciones.json'
    ,[KEYS.DESCARTES_TIPOS_METODOS]: 'descartes_tipos_metodos.json'
    ,[KEYS.TEXTOS_CONJUNCIONES_ADVERSATIVAS]: 'textos_conjunciones_adversativas.json'
    ,[KEYS.TEXTOS_COINCIDENCIAS]: 'textos_coincidencias.json'
  }

  /**
   * Nombres descriptivos para cada tipo de archivo
   */
  const FRIENDLY_NAMES = {
    [KEYS.HISTORIAS]: '📖 Historias',
    [KEYS.METODOS]: '🔪 Métodos',
    [KEYS.ESCENARIOS]: '🗺️ Escenarios',
    [KEYS.REFERENCIAS]: '🔗 Referencias',
    [KEYS.VINCULOS]: '👥 Vínculos',
    [KEYS.BOT_VINCULOS]: '🤖 Bot Vínculos',
    [KEYS.CLASE_VINCULOS]: '📋 Clase Vínculos',
    [KEYS.OBJETOS]: '📦 Objetos',
    [KEYS.RASGOS_PERSONALIDAD]: '😊 Rasgos Personalidad',
    [KEYS.RASGOS_FISICOS]: '👤 Rasgos Físicos',
    [KEYS.RASGOS_CLASE]: '🎭 Rasgos Clase',
    [KEYS.RASGOS_ECONOMICOS]: '💰 Rasgos Económicos',
    [KEYS.CLASE_RASGOS_ECONOMICOS]: '💵 Clase Rasgos Económicos',
    [KEYS.AFINIDADES]: '❤️ Afinidades',
    [KEYS.OCUPACIONES]: '💼 Ocupaciones',
    [KEYS.CLASE_OCUPACIONES]: '💼 Clase Ocupaciones',
    [KEYS.APELLIDOS]: '📝 Apellidos',
    [KEYS.EPILOGOS]: '📜 Epílogos',
    [KEYS.PERSONAJES]: '🎭 Personajes',
    [KEYS.COLECCIONES]: '📦 Colecciones'
    ,[KEYS.DESCARTES_TIPOS_METODOS]: '🗑️ Descartes Tipos Métodos'
    ,[KEYS.TEXTOS_CONJUNCIONES_ADVERSATIVAS]: '↔️ Textos Conjunciones Adversativas'
    ,[KEYS.TEXTOS_COINCIDENCIAS]: '🤝 Textos Coincidencias'
  }

  /**
   * Carga un archivo JSON del servidor
   */
  async function loadJsonFromServer(filename) {
    try {
      const data = await api.loadAsset(filename)
      return data
    } catch (error) {
      console.error(`Error cargando ${filename} del servidor:`, error)
      return null
    }
  }

  /**
   * Compara dos conjuntos de datos según su estructura
   */
  function compareData(localData, serverData, dataType, friendlyDataType) {
    // Determinar si es un array o un objeto wrapper
    let localArray, serverArray
    let isWrapper = false
    
    // Muchos archivos tienen estructura { "nombre_clave": [...] }
    if (localData && typeof localData === 'object' && !Array.isArray(localData)) {
      // Es un objeto wrapper, buscar el array principal
      const keys = Object.keys(localData)
      if (keys.length === 1 && Array.isArray(localData[keys[0]])) {
        localArray = localData[keys[0]]
        serverArray = serverData?.[keys[0]] || []
        isWrapper = true
      } else {
        // Es un objeto plano, comparar directamente
        return compareObjects(localData || {}, serverData || {})
      }
    } else {
      // Es directamente un array
      localArray = localData || []
      serverArray = serverData || []
    }

    return compareArrays(localArray, serverArray, dataType, friendlyDataType)
  }

  /**
   * Verifica la sincronización de todos los archivos JSON
   * @param {Object} localDataRefs - Objeto con refs de datos locales { historias, metodos, ... }
   * @returns {Array} Array de resultados con diferencias encontradas
   */
  async function checkSync(localDataRefs) {
    isSyncing.value = true
    syncResults.value = []
    hasConflicts.value = false

    try {
      const results = []

      // Verificar cada tipo de dato
      for (const [key, filename] of Object.entries(KEY_TO_FILENAME)) {
        const localRef = localDataRefs[key]
        if (!localRef) continue

        const localData = localRef.value
        const serverData = await loadJsonFromServer(filename)

        if (serverData === null) {
          // No se pudo cargar del servidor
          results.push({
            key,
            filename,
            friendlyName: FRIENDLY_NAMES[key],
            status: 'error',
            message: 'No se pudo cargar desde el servidor',
            differences: []
          })
          continue
        }

        // Comparar datos
        const differences = compareData(localData, serverData, '', FRIENDLY_NAMES[key])

        if (differences.length > 0) {
          hasConflicts.value = true
          results.push({
            key,
            filename,
            friendlyName: FRIENDLY_NAMES[key],
            status: 'different',
            message: `${differences.length} diferencia${differences.length > 1 ? 's' : ''} encontrada${differences.length > 1 ? 's' : ''}`,
            differences,
            localData,
            serverData
          })
        } else {
          results.push({
            key,
            filename,
            friendlyName: FRIENDLY_NAMES[key],
            status: 'synced',
            message: 'Sincronizado ✓',
            differences: []
          })
        }
      }

      syncResults.value = results
      return results

    } catch (error) {
      console.error('Error durante la verificación de sincronización:', error)
      throw error
    } finally {
      isSyncing.value = false
    }
  }

  /**
   * Aplica los cambios del servidor a los datos locales
   * @param {String} key - Clave del tipo de dato (ej: 'historias')
   * @param {Object} serverData - Datos del servidor a aplicar
   */
  function applyServerChanges(key, serverData) {
    return {
      key,
      data: serverData
    }
  }

  return {
    isSyncing,
    syncResults,
    hasConflicts,
    checkSync,
    applyServerChanges
  }
}
